import {
  ATTENDANCE_STATUS_LABELS,
  ATTENDANCE_STATUSES,
  DEFAULT_ATTENDANCE_PERIODS,
} from "@triedge/attendance-domain";
import type {
  AttendanceAuditEvent,
  AttendanceCellRecord,
  AttendanceControlPlaneConfig,
  AttendanceCsvExportResult,
  AttendanceFeatureBundle,
  AttendanceFeatureBundleOptions,
  AttendanceImportStatus,
  AttendanceMutationArgsMap,
  AttendanceMutationName,
  AttendanceMutationResultMap,
  AttendanceOverview,
  AttendanceOverviewRollingWindow,
  AttendanceQueryArgsMap,
  AttendanceQueryName,
  AttendanceQueryResultMap,
  AttendanceRecordsFilter,
  AttendanceRecordsGrid,
  AttendanceRouteManifest,
  AttendanceScope,
  AttendanceStatus,
  AttendanceTransport,
} from "../types";

const DEFAULT_SCOPE: AttendanceScope = {
  organizationId: "org-ystnj",
  schoolId: "ystnj-main",
};

const DEFAULT_DATE = "2026-05-13";
const DEFAULT_WEEK_START = "2026-05-11";
const DEFAULT_WEEK_END = "2026-05-15";
const MOCK_NOW = 1_778_688_000;

type StudentProfile = {
  studentId: string;
  studentName: string;
  familyId: string;
  classroomId: string;
};

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const pdfEscape = (value: unknown) =>
  String(value ?? "")
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const pdfDataUrlForRows = (
  title: string,
  rows: Array<Record<string, unknown>>,
) => {
  const rowLines = rows.length
    ? rows.map((row) =>
        Object.entries(row)
          .map(([key, value]) => `${key}: ${String(value ?? "")}`)
          .join(" | "),
      )
    : ["No rows in the selected Attendance scope."];
  const lines = [title, ...rowLines].slice(0, 48);
  const textOperators = lines
    .map((line, index) => `BT /F1 10 Tf 36 ${760 - index * 14} Td (${pdfEscape(line).slice(0, 180)}) Tj ET`)
    .join("\n");
  const objects = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj",
    "4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
    `5 0 obj << /Length ${textOperators.length} >> stream\n${textOperators}\nendstream endobj`,
  ];
  let offset = "%PDF-1.4\n".length;
  const xref = ["xref", `0 ${objects.length + 1}`, "0000000000 65535 f "];
  const body = objects
    .map((object) => {
      xref.push(`${String(offset).padStart(10, "0")} 00000 n `);
      offset += `${object}\n`.length;
      return object;
    })
    .join("\n");
  const xrefOffset = `%PDF-1.4\n${body}\n`.length;
  const pdf = `%PDF-1.4\n${body}\n${xref.join("\n")}\ntrailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return `data:application/pdf;base64,${btoa(pdf)}`;
};

const normalizeRouteBase = (routeBase: string) =>
  routeBase.length > 1 ? routeBase.replace(/\/+$/, "") : routeBase;

const routeManifestFor = (
  routeBase: string,
  studentsFamiliesRouteBase = "/school/students",
): AttendanceRouteManifest => {
  const base = normalizeRouteBase(routeBase);
  return {
    routeBase: base,
    overviewPath: base,
    recordsPath: `${base}/new-records`,
    newRecordsPath: `${base}/new-records`,
    rosterPath: `${base}/roster`,
    reportsPath: `${base}/reports`,
    teacherViewPath: `${base}/teacher-view`,
    studentHistoryPath: `${base}/students/:studentId`,
    studentsFamiliesRouteBase,
  };
};

const buildStudentHistoryRoute = (routeBase: string, studentId: string) =>
  `${normalizeRouteBase(routeBase)}/students/${encodeURIComponent(studentId)}`;

const createInitialConfig = (): AttendanceControlPlaneConfig => ({
  divisions: [
    { divisionId: "division-lower", label: "Lower School" },
    { divisionId: "division-middle", label: "Middle School" },
    { divisionId: "division-upper", label: "Upper School" },
  ],
  grades: [
    { gradeId: "grade-2", divisionId: "division-lower", label: "Grade 2" },
    { gradeId: "grade-5", divisionId: "division-middle", label: "Grade 5" },
    { gradeId: "grade-8", divisionId: "division-upper", label: "Grade 8" },
  ],
  classrooms: [
    {
      classroomId: "class-2a",
      gradeId: "grade-2",
      label: "2A",
      teacherId: "teacher-goldstein",
      teacherName: "Rachel Goldstein",
    },
    {
      classroomId: "class-2b",
      gradeId: "grade-2",
      label: "2B",
      teacherId: "teacher-silver",
      teacherName: "Talia Silver",
    },
    {
      classroomId: "class-5b",
      gradeId: "grade-5",
      label: "5B",
      teacherId: "teacher-bernstein",
      teacherName: "Michael Bernstein",
    },
    {
      classroomId: "class-8c",
      gradeId: "grade-8",
      label: "8C",
      teacherId: "teacher-cohen",
      teacherName: "Ari Cohen",
    },
  ],
  periods: DEFAULT_ATTENDANCE_PERIODS.map((period) => ({ ...period })),
  rosterMemberships: [
    {
      studentId: "student-naomi-benatar",
      studentName: "Naomi Benatar",
      familyId: "family-benatar",
      classroomId: "class-2a",
    },
    {
      studentId: "student-ezra-harari",
      studentName: "Ezra Harari",
      familyId: "family-harari",
      classroomId: "class-2a",
    },
    {
      studentId: "student-mia-abadi",
      studentName: "Mia Abadi",
      familyId: "family-abadi",
      classroomId: "class-2a",
    },
    {
      studentId: "student-yosef-levy",
      studentName: "Yosef Levy",
      familyId: "family-levy",
      classroomId: "class-2a",
    },
    {
      studentId: "student-avi-mizrahi",
      studentName: "Avi Mizrahi",
      familyId: "family-mizrahi",
      classroomId: "class-2b",
    },
    {
      studentId: "student-eden-rafael",
      studentName: "Eden Rafael",
      familyId: "family-rafael",
      classroomId: "class-2b",
    },
    {
      studentId: "student-leah-sasson",
      studentName: "Leah Sasson",
      familyId: "family-sasson",
      classroomId: "class-5b",
    },
    {
      studentId: "student-david-moyal",
      studentName: "David Moyal",
      familyId: "family-moyal",
      classroomId: "class-5b",
    },
    {
      studentId: "student-rina-cohen",
      studentName: "Rina Cohen",
      familyId: "family-cohen",
      classroomId: "class-8c",
    },
  ],
  schoolCalendar: {
    activeWeekdays: [1, 2, 3, 4, 5],
    defaultStatus: "present",
  },
  updatedAt: MOCK_NOW,
  updatedBy: "attendance-v1.2-mock",
});

const createStudentProfiles = (
  config: AttendanceControlPlaneConfig,
): StudentProfile[] =>
  config.rosterMemberships.map((membership) => ({
    studentId: membership.studentId,
    studentName: membership.studentName,
    familyId: membership.familyId ?? membership.studentId,
    classroomId: membership.classroomId,
  }));

const makeCellKey = ({
  organizationId,
  schoolId,
  schoolDate,
  classroomId,
  periodId,
  studentId,
}: Pick<
  AttendanceCellRecord,
  | "organizationId"
  | "schoolId"
  | "schoolDate"
  | "classroomId"
  | "periodId"
  | "studentId"
>) =>
  [
    organizationId,
    schoolId,
    schoolDate,
    classroomId,
    periodId,
    studentId,
  ].join("::");

const isoDate = (date: Date) => date.toISOString().slice(0, 10);

const weekDatesFor = (
  schoolDate: string,
  activeWeekdays: readonly number[] = [1, 2, 3, 4, 5],
) => {
  const date = new Date(`${schoolDate}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || !activeWeekdays.length) return [schoolDate];
  const sunday = new Date(date);
  sunday.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return Array.from({ length: 7 }, (_, offset) => {
    const next = new Date(sunday);
    next.setUTCDate(sunday.getUTCDate() + offset);
    return next;
  })
    .filter((next) => activeWeekdays.includes(next.getUTCDay()))
    .map(isoDate);
};

const rollingSchoolDatesEndingAt = (
  schoolDate: string,
  activeWeekdays: readonly number[] = [1, 2, 3, 4, 5],
  targetSchoolDayCount = 30,
) => {
  const end = new Date(`${schoolDate}T12:00:00.000Z`);
  if (Number.isNaN(end.getTime()) || !activeWeekdays.length) return [schoolDate];
  const dates: string[] = [];
  const cursor = new Date(end);
  while (dates.length < targetSchoolDayCount) {
    if (activeWeekdays.includes(cursor.getUTCDay())) {
      dates.push(isoDate(cursor));
    }
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return dates.reverse();
};

const attendanceRatePercentFor = (
  presentLateCount: number,
  expectedCount: number,
) => {
  return expectedCount > 0 ? Math.round((presentLateCount / expectedCount) * 100) : 100;
};

const CHRONIC_MIA_ABSENCE_DATES = new Set(rollingSchoolDatesEndingAt(DEFAULT_DATE).slice(-10));
const CHRONIC_RINA_ABSENCE_DATES = new Set(rollingSchoolDatesEndingAt(DEFAULT_DATE).slice(-9));

const statusForSeed = (
  studentId: string,
  periodId: string,
  schoolDate: string,
): { status: AttendanceStatus; note: string | null } => {
  if (studentId === "student-mia-abadi" && CHRONIC_MIA_ABSENCE_DATES.has(schoolDate)) {
    return { status: "absent", note: "Family travel note on file." };
  }
  if (studentId === "student-rina-cohen" && CHRONIC_RINA_ABSENCE_DATES.has(schoolDate)) {
    return { status: "absent", note: "Nurse confirmed dismissal from campus." };
  }
  if (schoolDate === DEFAULT_DATE && studentId === "student-ezra-harari" && periodId === "period-1") {
    return { status: "late", note: "Arrived after bus delay." };
  }
  if (schoolDate === DEFAULT_DATE && studentId === "student-yosef-levy" && periodId === "period-1") {
    return { status: "excused", note: "Doctor appointment." };
  }
  if (schoolDate === DEFAULT_DATE && studentId === "student-leah-sasson" && periodId === "period-4") {
    return { status: "excused", note: "Medical appointment." };
  }
  return { status: "present", note: null };
};

const createInitialCells = (
  scope: AttendanceScope,
  config: AttendanceControlPlaneConfig,
) => {
  const cells = new Map<string, AttendanceCellRecord>();
  const seedDates = rollingSchoolDatesEndingAt(
    DEFAULT_DATE,
    config.schoolCalendar.activeWeekdays,
  );
  const students = createStudentProfiles(config);

  for (const schoolDate of seedDates) {
    for (const student of students) {
      for (const period of config.periods) {
        const seeded = statusForSeed(student.studentId, period.periodId, schoolDate);
        const cell: AttendanceCellRecord = {
          ...scope,
          studentId: student.studentId,
          schoolDate,
          classroomId: student.classroomId,
          periodId: period.periodId,
          status: seeded.status,
          note: seeded.note,
          version: 1,
          submissionState:
            student.classroomId === "class-8c" && period.periodId === "period-7"
              ? "draft"
              : "submitted",
          submittedAt:
            student.classroomId === "class-8c" && period.periodId === "period-7"
              ? null
              : MOCK_NOW - 900,
          submittedBy:
            student.classroomId === "class-8c" && period.periodId === "period-7"
              ? null
              : "front-office",
          updatedAt: MOCK_NOW - 450,
          updatedBy: "front-office",
          sourceSurface: "seed",
        };
        cells.set(makeCellKey(cell), cell);
      }
    }
  }

  return cells;
};

const getGradeForClassroom = (
  config: AttendanceControlPlaneConfig,
  classroomId: string,
) => {
  const classroom = config.classrooms.find(
    (entry) => entry.classroomId === classroomId,
  );
  return classroom
    ? config.grades.find((entry) => entry.gradeId === classroom.gradeId) ?? null
    : null;
};

const getDivisionForClassroom = (
  config: AttendanceControlPlaneConfig,
  classroomId: string,
) => {
  const grade = getGradeForClassroom(config, classroomId);
  return grade
    ? config.divisions.find((entry) => entry.divisionId === grade.divisionId) ??
        null
    : null;
};

const periodSortOrder = (
  config: AttendanceControlPlaneConfig,
  periodId: string,
) => config.periods.find((period) => period.periodId === periodId)?.sortOrder ?? 0;

const dateInFilter = (
  schoolDate: string,
  filter: AttendanceRecordsFilter,
  config: AttendanceControlPlaneConfig,
) => {
  if (filter.dayMode === "day") {
    const date = new Date(`${filter.schoolDate}T12:00:00.000Z`);
    return (
      schoolDate === filter.schoolDate &&
      !Number.isNaN(date.getTime()) &&
      config.schoolCalendar.activeWeekdays.includes(date.getUTCDay())
    );
  }
  return weekDatesFor(filter.schoolDate, config.schoolCalendar.activeWeekdays).includes(schoolDate);
};

const cellMatchesFilter = (
  cell: AttendanceCellRecord,
  filter: AttendanceRecordsFilter,
  config: AttendanceControlPlaneConfig,
) => {
  if (
    cell.organizationId !== filter.organizationId ||
    cell.schoolId !== filter.schoolId ||
    !dateInFilter(cell.schoolDate, filter, config)
  ) {
    return false;
  }
  if (filter.classroomId && cell.classroomId !== filter.classroomId) {
    return false;
  }
  if (filter.periodId && cell.periodId !== filter.periodId) {
    return false;
  }
  const grade = getGradeForClassroom(config, cell.classroomId);
  if (filter.gradeId && grade?.gradeId !== filter.gradeId) return false;
  if (filter.divisionId && grade?.divisionId !== filter.divisionId) return false;
  return true;
};

const buildStudentLinks = (
  cells: AttendanceCellRecord[],
  config: AttendanceControlPlaneConfig,
  routeBase: string,
) => {
  const studentIds = new Set(cells.map((cell) => cell.studentId));
  return config.rosterMemberships
    .filter((membership) => studentIds.has(membership.studentId))
    .map((membership) => ({
      label: "Student Attendance History" as const,
      route: buildStudentHistoryRoute(routeBase, membership.studentId),
      studentId: membership.studentId,
      familyId: membership.familyId ?? null,
    }));
};

const createEmptyStatusCounts = () =>
  Object.fromEntries(ATTENDANCE_STATUSES.map((status) => [status, 0])) as Record<
    AttendanceStatus,
    number
  >;

const normalizeOverviewWindow = (value: unknown): AttendanceOverviewRollingWindow =>
  value === 7 || value === 30 || value === 60 || value === 90 ? value : 30;

const buildOverview = (
  scope: AttendanceScope,
  schoolDate: string,
  config: AttendanceControlPlaneConfig,
  cells: Map<string, AttendanceCellRecord>,
  routeBase: string,
  rollingSchoolDayWindow: AttendanceOverviewRollingWindow = 30,
): AttendanceOverview => {
  const parsedSchoolDate = new Date(`${schoolDate}T12:00:00.000Z`);
  const isActiveSchoolDate = !Number.isNaN(parsedSchoolDate.getTime())
    && config.schoolCalendar.activeWeekdays.includes(parsedSchoolDate.getUTCDay());
  const dayCells = Array.from(cells.values()).filter(
    (cell) =>
      isActiveSchoolDate &&
      cell.organizationId === scope.organizationId &&
      cell.schoolId === scope.schoolId &&
      cell.schoolDate === schoolDate,
  );
  const statusCounts = createEmptyStatusCounts();
  dayCells.forEach((cell) => {
    statusCounts[cell.status] += 1;
  });

  const expectedCellCount = isActiveSchoolDate
    ? config.rosterMemberships.length * config.periods.length
    : 0;
  const divisionBreakdown = config.divisions.map((division) => {
    const divisionCells = dayCells.filter(
      (cell) =>
        getDivisionForClassroom(config, cell.classroomId)?.divisionId ===
        division.divisionId,
    );
    const divisionClassroomIds = new Set(
      config.classrooms
        .filter((classroom) =>
          getGradeForClassroom(config, classroom.classroomId)?.divisionId === division.divisionId,
        )
        .map((classroom) => classroom.classroomId),
    );
    const divisionExpectedCellCount = isActiveSchoolDate
      ? config.rosterMemberships.filter((membership) => divisionClassroomIds.has(membership.classroomId)).length
        * config.periods.length
      : 0;
    const absenceMetricCount = divisionCells.filter(
      (cell) => cell.status === "absent" || cell.status === "excused",
    ).length;
    const lateCount = divisionCells.filter((cell) => cell.status === "late").length;
    const presentLateCount = divisionCells.filter(
      (cell) => cell.status === "present" || cell.status === "late",
    ).length;
    return {
      divisionId: division.divisionId,
      divisionLabel: division.label,
      attendanceRatePercent: attendanceRatePercentFor(
        presentLateCount,
        divisionExpectedCellCount,
      ),
      presentLateCount,
      expectedCellCount: divisionExpectedCellCount,
      absenceMetricCount,
      lateCount,
    };
  });

  const notSubmittedRows = isActiveSchoolDate
    ? config.classrooms.flatMap((classroom) =>
        config.periods
          .filter((period) =>
            dayCells.some(
              (cell) =>
                cell.classroomId === classroom.classroomId &&
                cell.periodId === period.periodId &&
                cell.submissionState === "draft",
            ),
          )
          .map((period) => {
            const grade = getGradeForClassroom(config, classroom.classroomId);
            return {
              classroomId: classroom.classroomId,
              classroomLabel: classroom.label,
              teacherName: classroom.teacherName ?? null,
              periodId: period.periodId,
              periodLabel: period.label,
              routeFilter: {
                ...scope,
                schoolDate,
                dayMode: "day" as const,
                divisionId: grade?.divisionId ?? null,
                gradeId: grade?.gradeId ?? null,
                classroomId: classroom.classroomId,
                periodId: period.periodId,
              },
            };
          }),
      )
    : [];
  const rollingDates = new Set(
    rollingSchoolDatesEndingAt(
      schoolDate,
      config.schoolCalendar.activeWeekdays,
      rollingSchoolDayWindow,
    ),
  );
  const rollingCells = Array.from(cells.values()).filter(
    (cell) =>
      cell.organizationId === scope.organizationId &&
      cell.schoolId === scope.schoolId &&
      rollingDates.has(cell.schoolDate),
  );
  const chronicAbsenceRows = config.rosterMemberships
    .map((membership) => {
      const studentCells = rollingCells.filter((cell) => cell.studentId === membership.studentId);
      const absenceMetricCount = new Set(
        studentCells
          .filter((cell) => cell.status === "absent" || cell.status === "excused")
          .map((cell) => cell.schoolDate),
      ).size;
      const absenceRatePercent = rollingDates.size
        ? Math.round((absenceMetricCount / rollingDates.size) * 100)
        : 0;
      const classroom = config.classrooms.find((entry) => entry.classroomId === membership.classroomId);
      const grade = classroom ? getGradeForClassroom(config, classroom.classroomId) : null;
      return {
        studentId: membership.studentId,
        studentName: membership.studentName,
        gradeLabel: grade?.label ?? "Grade",
        classroomLabel: classroom?.label ?? membership.classroomId,
        rollingSchoolDays: rollingDates.size,
        absenceMetricCount,
        absenceRatePercent,
        thresholdPercent: 10 as const,
        linkTargetRoute: buildStudentHistoryRoute(routeBase, membership.studentId),
      };
    })
    .filter((row) => row.absenceMetricCount > 0 && row.absenceRatePercent >= row.thresholdPercent)
    .sort((left, right) =>
      right.absenceRatePercent - left.absenceRatePercent
      || right.absenceMetricCount - left.absenceMetricCount
      || left.studentName.localeCompare(right.studentName),
    );

  return {
    schoolDate,
    metrics: {
      statusCounts,
      absenceMetricCount: statusCounts.absent + statusCounts.excused,
      lateCount: statusCounts.late,
      recordedCellCount: dayCells.length,
      expectedCellCount,
      attendanceRatePercent: attendanceRatePercentFor(
        statusCounts.present + statusCounts.late,
        expectedCellCount,
      ),
      excusedRatePercent: expectedCellCount
        ? Math.round((statusCounts.excused / expectedCellCount) * 100)
        : 0,
    },
    chronicAbsenceRows,
    divisionBreakdown,
    notSubmittedRows,
  };
};

const emptyOverviewExportRow = (
  schoolDate: string,
  rollingSchoolDays: AttendanceOverviewRollingWindow,
) => ({
  Date: schoolDate,
  Present: "",
  Absent: "",
  Late: "",
  Excused: "",
  AttendanceRate: "",
  ExcusedRate: "",
  RollingSchoolDays: rollingSchoolDays,
  ChronicThreshold: ">= 10%",
  ChronicAbsenceAlerts: "",
  Section: "",
  Student: "",
  Grade: "",
  AbsenceRate: "",
  AbsenceMetricCount: "",
  Division: "",
  DivisionAttendanceRate: "",
  NotSubmittedClassroom: "",
  NotSubmittedPeriod: "",
  NotSubmittedTeacher: "",
});

const buildOverviewExportRows = (
  overview: AttendanceOverview,
  rollingSchoolDays: AttendanceOverviewRollingWindow,
): Array<Record<string, unknown>> => {
  const baseRow = emptyOverviewExportRow(overview.schoolDate, rollingSchoolDays);
  return [
    {
      ...baseRow,
      Present: overview.metrics.statusCounts.present,
      Absent: overview.metrics.statusCounts.absent,
      Late: overview.metrics.statusCounts.late,
      Excused: overview.metrics.statusCounts.excused,
      AttendanceRate: overview.metrics.attendanceRatePercent,
      ExcusedRate: overview.metrics.excusedRatePercent,
      ChronicAbsenceAlerts: overview.chronicAbsenceRows.length,
      Section: "Summary",
    },
    ...overview.chronicAbsenceRows.map((row) => ({
      ...baseRow,
      RollingSchoolDays: row.rollingSchoolDays,
      ChronicThreshold: `>= ${row.thresholdPercent}%`,
      Section: "Chronic Absence Alert",
      Student: row.studentName,
      Grade: row.gradeLabel,
      AbsenceRate: row.absenceRatePercent,
      AbsenceMetricCount: row.absenceMetricCount,
    })),
    ...overview.divisionBreakdown.map((row) => ({
      ...baseRow,
      Section: "Division Breakdown",
      Division: row.divisionLabel,
      DivisionAttendanceRate: row.attendanceRatePercent,
      AbsenceMetricCount: row.absenceMetricCount,
      Late: row.lateCount,
    })),
    ...overview.notSubmittedRows.map((row) => ({
      ...baseRow,
      Section: "Not Submitted",
      NotSubmittedClassroom: row.classroomLabel,
      NotSubmittedPeriod: row.periodLabel,
      NotSubmittedTeacher: row.teacherName ?? "",
    })),
  ];
};

const csvEscape = (value: unknown) => {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const safeExportSlug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "attendance";

const csvFromRows = (rows: Array<Record<string, unknown>>) => {
  const headers = Array.from(rows.reduce((set, row) => {
    Object.keys(row).forEach((key) => set.add(key));
    return set;
  }, new Set<string>()));
  return [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => csvEscape(row[header])).join(",")),
  ].join("\n");
};

const buildRecordsGrid = (
  filter: AttendanceRecordsFilter,
  config: AttendanceControlPlaneConfig,
  cells: Map<string, AttendanceCellRecord>,
  routeBase: string,
): AttendanceRecordsGrid => {
  const matchingCells = Array.from(cells.values())
    .filter((cell) => cellMatchesFilter(cell, filter, config))
    .sort(
      (left, right) =>
        left.schoolDate.localeCompare(right.schoolDate) ||
        left.classroomId.localeCompare(right.classroomId) ||
        left.studentId.localeCompare(right.studentId) ||
        left.periodId.localeCompare(right.periodId),
    );

  return {
    filter: clone(filter),
    cells: clone(matchingCells),
    studentLinks: buildStudentLinks(
      matchingCells,
      config,
      routeBase,
    ),
    overwriteWarning: matchingCells.some(
      (cell) => cell.submissionState === "submitted",
    )
      ? "Some visible records are already submitted. Saving a cell creates a new version."
      : null,
  };
};

const createCsvExport = (
  input: AttendanceMutationArgsMap["exportAttendanceRecordsCsv"]["input"],
  config: AttendanceControlPlaneConfig,
  cells: Map<string, AttendanceCellRecord>,
): AttendanceCsvExportResult => {
  if (input.scope === "overview-current-date") {
    const rollingSchoolDayWindow = normalizeOverviewWindow(input.rollingSchoolDayWindow);
    const overview = buildOverview(
      {
        organizationId: input.organizationId,
        schoolId: input.schoolId,
      },
      input.schoolDate,
      config,
      cells,
      "/school/attendance",
      rollingSchoolDayWindow,
    );
    return {
      exportId: `attendance-csv-${overview.schoolDate}`,
      scope: input.scope,
      fileName: `attendance-overview-current-date-${overview.schoolDate}.csv`,
      delivery: "inline",
      csvText: csvFromRows(
        buildOverviewExportRows(overview, rollingSchoolDayWindow).map((row) => ({
          "Export Scope": input.scope,
          ...row,
        })),
      ),
    };
  }
  const recordsFilter =
    input.recordsFilter;
  const records = Array.from(cells.values()).filter((cell) =>
    cellMatchesFilter(cell, recordsFilter, config),
  );
  const includeNotes = input.includeNotes ?? false;
  const header = includeNotes
    ? "studentName,status,note,schoolDate,classroom,period"
    : "studentName,status,schoolDate,classroom,period";
  const rows = records.map((cell) => {
    const student = config.rosterMemberships.find(
      (membership) => membership.studentId === cell.studentId,
    );
    const classroom = config.classrooms.find(
      (entry) => entry.classroomId === cell.classroomId,
    );
    const period = config.periods.find((entry) => entry.periodId === cell.periodId);
    const baseValues = [
      student?.studentName ?? cell.studentId,
      ATTENDANCE_STATUS_LABELS[cell.status].label,
      cell.schoolDate,
      classroom?.label ?? cell.classroomId,
      period?.label ?? cell.periodId,
    ];
    const values = includeNotes
      ? [baseValues[0], baseValues[1], cell.note ?? "", ...baseValues.slice(2)]
      : baseValues;
    return values.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",");
  });

  return {
    exportId: `attendance-csv-${recordsFilter.schoolDate}`,
    scope: input.scope,
    fileName: `attendance-records-current-filter-${recordsFilter.schoolDate}.csv`,
    delivery: "inline",
    csvText: [`Export Scope,${header}`, ...rows.map((row) => `${input.scope},${row}`)].join("\n"),
  };
};

const buildStudentHistory = (
  input: AttendanceQueryArgsMap["getAttendanceStudentHistory"]["input"],
  config: AttendanceControlPlaneConfig,
  cells: Map<string, AttendanceCellRecord>,
): AttendanceQueryResultMap["getAttendanceStudentHistory"] => {
  const student = config.rosterMemberships.find(
    (membership) => membership.studentId === input.studentId,
  );
  const studentCells = Array.from(cells.values())
    .filter(
      (cell) =>
        cell.organizationId === input.organizationId &&
        cell.schoolId === input.schoolId &&
        cell.studentId === input.studentId &&
        cell.schoolDate >= input.fromSchoolDate &&
        cell.schoolDate <= input.toSchoolDate,
    )
    .sort(
      (left, right) =>
        right.schoolDate.localeCompare(left.schoolDate) ||
        periodSortOrder(config, left.periodId) - periodSortOrder(config, right.periodId) ||
        left.classroomId.localeCompare(right.classroomId),
    );
  const summaryTotals = createEmptyStatusCounts();
  studentCells.forEach((cell) => {
    summaryTotals[cell.status] += 1;
  });
  const absenceMetricCount = summaryTotals.absent + summaryTotals.excused;
  const attendanceRatePercent = attendanceRatePercentFor(
    summaryTotals.present + summaryTotals.late,
    studentCells.length,
  );

  return {
    studentId: input.studentId,
    studentName: student?.studentName ?? input.studentId,
    route: buildStudentHistoryRoute("/school/attendance", input.studentId),
    range: {
      fromSchoolDate: input.fromSchoolDate,
      toSchoolDate: input.toSchoolDate,
    },
    summaryTotals: {
      ...summaryTotals,
      absenceMetricCount,
      attendanceRatePercent,
    },
    rows: studentCells.map((cell) => ({
      schoolDate: cell.schoolDate,
      classroomId: cell.classroomId,
      classroomLabel:
        config.classrooms.find((classroom) => classroom.classroomId === cell.classroomId)
          ?.label ?? cell.classroomId,
      periodId: cell.periodId,
      periodLabel:
        config.periods.find((period) => period.periodId === cell.periodId)?.label ??
        cell.periodId,
      status: cell.status,
      note: cell.note,
      version: cell.version,
      editable: false,
    })),
  };
};

const createContextExport = (
  input: AttendanceMutationArgsMap["exportAttendanceContext"]["input"],
  config: AttendanceControlPlaneConfig,
  cells: Map<string, AttendanceCellRecord>,
): AttendanceMutationResultMap["exportAttendanceContext"] => {
  const exportId = `attendance-context-${input.context}`;
  const base = {
    exportId,
    context: input.context,
    format: input.format,
    fileName: `attendance-${safeExportSlug(input.context)}-${exportId}.${input.format}`,
  };
  if (input.format === "pdf") {
    const result = createCsvExport(
      input.context === "overview"
        ? {
            scope: "overview-current-date",
            organizationId: input.organizationId,
            schoolId: input.schoolId,
            schoolDate: input.schoolDate,
            rollingSchoolDayWindow: input.rollingSchoolDayWindow,
            includeNotes: input.includeNotes,
          }
        : {
            scope: "records-current-filter",
            recordsFilter: input.recordsFilter,
            includeNotes: input.includeNotes,
          },
      config,
      cells,
    );
    const rows = result.delivery === "inline"
      ? result.csvText.split("\n").map((line) => ({ line }))
      : [];
    return {
      ...base,
      delivery: "download-url",
      downloadUrl: pdfDataUrlForRows(`Attendance ${input.context} export`, rows),
      csvText: null,
    };
  }
  const result = createCsvExport(
    input.context === "overview"
      ? {
          scope: "overview-current-date",
          organizationId: input.organizationId,
          schoolId: input.schoolId,
          schoolDate: input.schoolDate,
          rollingSchoolDayWindow: input.rollingSchoolDayWindow,
          includeNotes: input.includeNotes,
        }
      : {
          scope: "records-current-filter",
          recordsFilter: input.recordsFilter,
          includeNotes: input.includeNotes,
        },
    config,
    cells,
  );
  const csvText = result.delivery === "inline" ? result.csvText : "";
  return {
    ...base,
    delivery: "inline",
    csvText,
    downloadUrl: null,
  };
};

export const createAttendanceFeatureBundle = ({
  routeBase,
  transport,
  extractedFrom = "package-owned:attendance-v1.2",
  scope,
  studentsFamiliesRouteBase,
}: AttendanceFeatureBundleOptions): AttendanceFeatureBundle => ({
  id: "attendance",
  transport,
  extractedFrom,
  routeManifest: routeManifestFor(routeBase, studentsFamiliesRouteBase),
  runtimeScope: {
    organizationId: scope?.organizationId ?? DEFAULT_SCOPE.organizationId,
    schoolId: scope?.schoolId ?? DEFAULT_SCOPE.schoolId,
  },
  teacherPreview: { previewOnly: true, writesAllowed: false },
});

export const createMockAttendanceTransport = ({
  scope = DEFAULT_SCOPE,
  studentsFamiliesRouteBase = "/school/students",
  persistenceKey,
}: {
  scope?: AttendanceScope;
  studentsFamiliesRouteBase?: string;
  persistenceKey?: string;
} = {}): AttendanceTransport => {
  void studentsFamiliesRouteBase;
  const readPersistedState = (): {
    config?: AttendanceControlPlaneConfig;
    cells?: AttendanceCellRecord[];
  } => {
    if (!persistenceKey || typeof window === "undefined" || !window.localStorage) {
      return {};
    }
    try {
      const raw = window.localStorage.getItem(persistenceKey);
      return raw ? JSON.parse(raw) as { config?: AttendanceControlPlaneConfig; cells?: AttendanceCellRecord[] } : {};
    } catch {
      return {};
    }
  };
  const persistedState = readPersistedState();
  let config = persistedState.config ?? createInitialConfig();
  const cells = createInitialCells(scope, config);
  for (const cell of persistedState.cells ?? []) {
    if (cell.organizationId === scope.organizationId && cell.schoolId === scope.schoolId) {
      cells.set(makeCellKey(cell), cell);
    }
  }
  const persistFixtureState = () => {
    if (!persistenceKey || typeof window === "undefined" || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(
      persistenceKey,
      JSON.stringify({
        config,
        cells: Array.from(cells.values()),
      }),
    );
  };
  const importStatuses = new Map<string, AttendanceImportStatus>([
    [
      "import-demo-1",
      {
        importId: "import-demo-1",
        status: "completed",
        processedRows: 128,
        failedRows: 0,
        errors: [],
      },
    ],
  ]);
  const auditEvents: AttendanceAuditEvent[] = [
    {
      ...scope,
      auditId: "audit-seed-1",
      action: "seed",
      actorId: "attendance-v1.2-mock",
      occurredAt: MOCK_NOW - 1_200,
      schoolDate: DEFAULT_DATE,
      sourceSurface: "seed",
    },
  ];

  const ensureDefaultCellsForFilter = (filter: AttendanceRecordsFilter) => {
    const schoolDates =
      filter.dayMode === "week"
        ? weekDatesFor(filter.schoolDate, config.schoolCalendar.activeWeekdays)
        : [filter.schoolDate];
    let addedCell = false;

    for (const schoolDate of schoolDates) {
      const parsedDate = new Date(`${schoolDate}T12:00:00.000Z`);
      if (
        Number.isNaN(parsedDate.getTime()) ||
        !config.schoolCalendar.activeWeekdays.includes(parsedDate.getUTCDay())
      ) {
        continue;
      }
      for (const membership of config.rosterMemberships) {
        for (const period of config.periods) {
          const candidate: AttendanceCellRecord = {
            ...scope,
            studentId: membership.studentId,
            schoolDate,
            classroomId: membership.classroomId,
            periodId: period.periodId,
            status: config.schoolCalendar.defaultStatus,
            note: null,
            version: 0,
            submissionState: "draft",
            submittedAt: null,
            submittedBy: null,
            updatedAt: MOCK_NOW,
            updatedBy: "attendance-ui-mock",
            sourceSurface: "seed",
          };
          if (!cellMatchesFilter(candidate, filter, config)) {
            continue;
          }
          const key = makeCellKey(candidate);
          if (!cells.has(key)) {
            cells.set(key, candidate);
            addedCell = true;
          }
        }
      }
    }

    if (addedCell) {
      persistFixtureState();
    }
  };

  const matchingCells = (filter: AttendanceRecordsFilter) => {
    ensureDefaultCellsForFilter(filter);
    return Array.from(cells.values()).filter((cell) =>
      cellMatchesFilter(cell, filter, config),
    );
  };

  const unrecordedClassroomIdsFor = (schoolDate: string) => {
    const dayCells = matchingCells({
      ...scope,
      schoolDate,
      dayMode: "day",
    });
    return config.classrooms
      .filter((classroom) =>
        dayCells.some(
          (cell) =>
            cell.classroomId === classroom.classroomId &&
            cell.submissionState !== "submitted",
        ),
      )
      .map((classroom) => classroom.classroomId);
  };

  const assertWritableClassroomScope = (
    filter: Pick<AttendanceRecordsFilter, "classroomId">,
  ) => {
    if (!filter.classroomId) {
      throw new Error("Attendance write actions require a classroom scope.");
    }
  };

  const saveCell = (
    input: AttendanceMutationArgsMap["saveAttendanceRecordCell"]["input"],
  ) => {
    const key = makeCellKey(input);
    const existing = cells.get(key);
    const cell: AttendanceCellRecord = {
      ...input,
      note: input.note ?? existing?.note ?? null,
      version: (existing?.version ?? 0) + 1,
      submissionState: existing?.submissionState ?? "draft",
      submittedAt: existing?.submittedAt ?? null,
      submittedBy: existing?.submittedBy ?? null,
      updatedAt: MOCK_NOW,
      updatedBy: "attendance-ui-mock",
      sourceSurface: "records",
    };
    const changed =
      !existing ||
      existing.status !== cell.status ||
      existing.note !== cell.note;
    cells.set(key, cell);
    persistFixtureState();
    auditEvents.unshift({
      ...input,
      auditId: `audit-${auditEvents.length + 1}`,
      action: existing?.note !== cell.note ? "note" : "update",
      actorId: "attendance-ui-mock",
      occurredAt: MOCK_NOW,
      previousStatus: existing?.status ?? null,
      nextStatus: cell.status,
      previousNote: existing?.note ?? null,
      nextNote: cell.note,
      sourceSurface: "records",
    });
    return { cell: clone(cell), changed };
  };

  const applyBulkStatusAction = (
    filter: AttendanceRecordsFilter &
      Partial<
        Pick<
          AttendanceMutationArgsMap["applyAttendanceBulkAction"]["input"],
          "action" | "studentId" | "columnSchoolDate" | "columnPeriodId"
        >
      >,
  ) => {
    assertWritableClassroomScope(filter);
    if ((filter.action === "mark-row-present" || filter.action === "reset-row") && !filter.studentId) {
      throw new Error("Attendance row bulk action requires a student scope.");
    }
    if (
      (filter.action === "mark-column-present" || filter.action === "reset-column") &&
      !filter.columnSchoolDate &&
      !filter.columnPeriodId
    ) {
      throw new Error("Attendance column bulk action requires a column scope.");
    }
    let changedCellCount = 0;
    let retainedNotesCount = 0;
    matchingCells(filter).filter((cell) => {
      if (filter.studentId && cell.studentId !== filter.studentId) return false;
      if (filter.columnSchoolDate && cell.schoolDate !== filter.columnSchoolDate) {
        return false;
      }
      if (filter.columnPeriodId && cell.periodId !== filter.columnPeriodId) {
        return false;
      }
      return true;
    }).forEach((cell) => {
      const targetStatus = filter.action?.startsWith("reset-")
        ? config.schoolCalendar.defaultStatus
        : "present";
      if (cell.note) retainedNotesCount += 1;
      if (cell.status !== targetStatus) changedCellCount += 1;
      cells.set(makeCellKey(cell), {
        ...cell,
        status: targetStatus,
        version: cell.version + 1,
        updatedAt: MOCK_NOW,
        updatedBy: "attendance-ui-mock",
        sourceSurface: "records",
      });
    });
    persistFixtureState();
    return { changedCellCount, retainedNotesCount };
  };

  const queryHandlers: {
    [Name in AttendanceQueryName]: (
      args: AttendanceQueryArgsMap[Name],
    ) => Promise<AttendanceQueryResultMap[Name]>;
  } = {
    async getAttendanceCaptureContext(args) {
      const currentSchoolDate = args.input.schoolDate ?? DEFAULT_DATE;
      const weekDates = weekDatesFor(
        currentSchoolDate,
        config.schoolCalendar.activeWeekdays,
      );
      const unrecordedClassroomIds = unrecordedClassroomIdsFor(currentSchoolDate);
      return {
        config: clone(config),
        currentSchoolDate,
        currentWeekStart: weekDates[0] ?? DEFAULT_WEEK_START,
        currentWeekEnd: weekDates[weekDates.length - 1] ?? DEFAULT_WEEK_END,
        firstUnrecordedClassroomId: unrecordedClassroomIds[0] ?? null,
        unrecordedClassroomIds,
        teacherPreview: { previewOnly: true, writesAllowed: false },
      };
    },
    async getAttendanceOverview(args) {
      ensureDefaultCellsForFilter({
        ...args.input,
        schoolDate: args.input.schoolDate,
        dayMode: "day",
      });
      return clone(
        buildOverview(
          args.input,
          args.input.schoolDate,
          config,
          cells,
          "/school/attendance",
          normalizeOverviewWindow(args.input.rollingSchoolDayWindow),
        ),
      );
    },
    async listAttendanceRecords(args) {
      ensureDefaultCellsForFilter(args.input);
      return buildRecordsGrid(
        args.input,
        config,
        cells,
        "/school/attendance",
      );
    },
    async getAttendanceStudentHistory(args) {
      return clone(buildStudentHistory(args.input, config, cells));
    },
    async getAttendanceControlPlaneConfig() {
      return clone(config);
    },
    async listAttendanceAuditEvents(args) {
      const limited = auditEvents
        .filter(
          (event) =>
            event.organizationId === args.input.organizationId &&
            event.schoolId === args.input.schoolId &&
            (!args.input.action || event.action === args.input.action),
        )
        .slice(0, args.input.limit ?? 25);
      return { events: clone(limited), nextToken: null };
    },
    async getAttendanceImportStatus(args) {
      return clone(
        importStatuses.get(args.input.importId) ?? {
          importId: args.input.importId,
          status: "pending",
          processedRows: 0,
          failedRows: 0,
          errors: [],
        },
      );
    },
  };

  const mutationHandlers: {
    [Name in AttendanceMutationName]: (
      args: AttendanceMutationArgsMap[Name],
    ) => Promise<AttendanceMutationResultMap[Name]>;
  } = {
    async saveAttendanceRecordCell(args) {
      return saveCell(args.input);
    },
    async applyAttendanceBulkAction(args) {
      const result = applyBulkStatusAction(args.input);
      auditEvents.unshift({
        ...args.input,
        auditId: `audit-${auditEvents.length + 1}`,
        action: "bulk",
        actorId: "attendance-ui-mock",
        occurredAt: MOCK_NOW,
        sourceSurface: "records",
        batchId: args.input.action,
      });
      return { ...result, skippedCellCount: 0 };
    },
    async submitAttendanceRecordScope(args) {
      const filter: AttendanceRecordsFilter = {
        organizationId: args.input.organizationId,
        schoolId: args.input.schoolId,
        schoolDate: args.input.schoolDate,
        dayMode: args.input.dayMode,
        classroomId: args.input.classroomId,
        periodId: args.input.periodId ?? null,
      };
      assertWritableClassroomScope(filter);
      let submittedCellCount = 0;
      matchingCells(filter).forEach((cell) => {
        submittedCellCount += 1;
        cells.set(makeCellKey(cell), {
          ...cell,
          submissionState: "submitted",
          submittedAt: MOCK_NOW,
          submittedBy: "attendance-ui-mock",
          updatedAt: MOCK_NOW,
          updatedBy: "attendance-ui-mock",
        });
      });
      persistFixtureState();
      return {
        submittedAt: MOCK_NOW,
        submittedBy: "attendance-ui-mock",
        submittedCellCount,
      };
    },
    async resetAttendanceRecordScope(args) {
      const result = applyBulkStatusAction({
        ...args.input,
        action: "reset-visible-view",
      });
      auditEvents.unshift({
        ...args.input,
        auditId: `audit-${auditEvents.length + 1}`,
        action: "reset",
        actorId: "attendance-ui-mock",
        occurredAt: MOCK_NOW,
        sourceSurface: "records",
      });
      return result;
    },
    async updateAttendanceControlPlaneConfig(args) {
      config = {
        ...config,
        ...args.input.patch,
        updatedAt: MOCK_NOW,
        updatedBy: "attendance-ui-mock",
      };
      persistFixtureState();
      return clone(config);
    },
    async requestAttendanceImportUpload(args) {
      const importId = `import-${args.input.fileName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
      importStatuses.set(importId, {
        importId,
        status: "pending",
        processedRows: 0,
        failedRows: 0,
        errors: [],
      });
      return {
        importId,
        uploadUrl: `https://uploads.example.test/${importId}`,
        expiresAt: MOCK_NOW + 900,
      };
    },
    async startAttendanceImport(args) {
      const status: AttendanceImportStatus = {
        importId: args.input.importId,
        status: args.input.mode === "validate-only" ? "completed" : "running",
        processedRows: args.input.mode === "validate-only" ? 42 : 0,
        failedRows: 0,
        errors: [],
      };
      importStatuses.set(args.input.importId, status);
      return clone(status);
    },
    async exportAttendanceRecordsCsv(args) {
      if (args.input.scope === "records-current-filter") {
        ensureDefaultCellsForFilter(args.input.recordsFilter);
      } else {
        ensureDefaultCellsForFilter({
          ...scope,
          schoolDate: args.input.schoolDate,
          dayMode: "day",
        });
      }
      return createCsvExport(args.input, config, cells);
    },
    async exportAttendanceContext(args) {
      if (args.input.context === "overview") {
        ensureDefaultCellsForFilter({
          ...scope,
          schoolDate: args.input.schoolDate,
          dayMode: "day",
        });
      } else {
        ensureDefaultCellsForFilter(args.input.recordsFilter);
      }
      return createContextExport(args.input, config, cells);
    },
  };

  return {
    query(name, args) {
      return queryHandlers[name](args as never) as Promise<
        AttendanceQueryResultMap[typeof name]
      >;
    },
    mutate(name, args) {
      return mutationHandlers[name](args as never) as Promise<
        AttendanceMutationResultMap[typeof name]
      >;
    },
  };
};

export const createMockAttendanceFeatureBundle = (
  routeBase = "/school/attendance",
) => {
  const transport = createMockAttendanceTransport();
  return createAttendanceFeatureBundle({
    routeBase,
    transport,
  });
};
